import React, { useEffect, useRef, useState } from "react";

import { useHtmlPageSize } from "./useHtmlPageSize";

export default function HtmlArtifactFrame({
  url,
  thumbnailUrl,
  title,
  fit = "width",
  maxHeightVh,
  loading = "eager",
  className = "",
  frameClassName = "",
  frameStyle,
  onLoad,
  onError,
}) {
  const { size, ready } = useHtmlPageSize(url, thumbnailUrl);
  const containerRef = useRef(null);
  const onLoadRef = useRef(onLoad);
  const [scale, setScale] = useState(1);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const visible = ready && frameLoaded;

  onLoadRef.current = onLoad;

  useEffect(() => {
    setFrameLoaded(false);
  }, [url]);

  useEffect(() => {
    if (visible) onLoadRef.current?.();
  }, [visible]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const update = () => {
      const byWidth = container.clientWidth / size.width;
      const boxHeight =
        fit === "contain"
          ? container.clientHeight
          : maxHeightVh && (window.innerHeight * maxHeightVh) / 100;
      const byHeight = boxHeight > 0 ? boxHeight / size.height : Infinity;
      const next = Math.min(byWidth, byHeight);
      setScale(Number.isFinite(next) && next > 0 ? next : 1);
    };
    update();
    window.addEventListener("resize", update);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(container);
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [size, fit, maxHeightVh]);

  return (
    <div
      ref={containerRef}
      className={`w-full flex items-center justify-center ${className}`}
    >
      <div
        className={`relative overflow-hidden flex-shrink-0 ${
          visible ? "" : "bg-cfSearchContainer animate-pulse"
        } ${frameClassName}`}
        style={{ width: size.width * scale, height: size.height * scale }}
        aria-busy={visible ? undefined : "true"}
      >
        <iframe
          src={url}
          title={title}
          sandbox=""
          loading={loading}
          scrolling="no"
          onLoad={() => setFrameLoaded(true)}
          onError={(event) => {
            setFrameLoaded(true);
            onError?.(event);
          }}
          style={{
            width: size.width,
            height: size.height,
            border: 0,
            background: "#fff",
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            position: "absolute",
            top: 0,
            left: 0,
            visibility: visible ? "visible" : "hidden",
            ...frameStyle,
          }}
        />
      </div>
    </div>
  );
}
